import { colorsForCategory } from '../listing/categoryColors';
import type { StudyCategorySlug } from 'content/science/studyTypes';
import type { AskCategoryId } from './askData';

/* Colour for the Ask Our Data track.
 *
 * Two independent scales, and they are not interchangeable.
 *
 *   ART is the placeholder photograph — a two-stop ramp per subject, deep and
 *   desaturated, because it stands in for a graded photo rather than for a
 *   category swatch. It is the only dark surface on an otherwise light band:
 *   the band stopped being black on 21 Aug 2026 and the cards carry the dark.
 *
 *   DOT is the category swatch on the pill, at full chroma, because a 8px
 *   square has to be identifiable against white at a glance.
 *
 * Both are lifted verbatim from the mockup's `:root`; nothing here is mixed.
 */

export interface ArtRamp {
  /** The deep end, bottom-right of the ramp. */
  from: string;
  /** The lifted end, top-left, where the key light lands. */
  to: string;
}

export const ART_RAMP: Record<AskCategoryId, ArtRamp> = {
  heart: { from: '#3d0f18', to: '#8c2b3c' },
  sleep: { from: '#0e1a2c', to: '#2f4f7d' },
  women: { from: '#2c1122', to: '#8a3a60' },
  metabolic: { from: '#2a1808', to: '#a06a24' },
  environment: { from: '#0b1f19', to: '#1f6b52' },
};

/* Read from the shared science palette, never declared here.
 *
 * These five were a private Tailwind-ish set, and heart was #dc2626 — the
 * exact hex categoryColors.ts documents removing, because a red tag reads as
 * an error state rather than a category. So the same subject was one colour on
 * a question card, another on a publication card and a third in search: three
 * answers to "what colour is Heart health".
 *
 * The Ask ids are the shared category slugs under shorter names, so the
 * translation is a rename and nothing here is a judgement call. */
export const SHARED_SLUG: Record<AskCategoryId, StudyCategorySlug> = {
  heart: 'heart-health',
  sleep: 'sleep',
  metabolic: 'metabolic-health',
  women: 'womens-health',
  environment: 'environment',
};

export const CATEGORY_DOT: Record<AskCategoryId, string> = Object.fromEntries(
  (Object.keys(SHARED_SLUG) as AskCategoryId[]).map((id) => [
    id,
    colorsForCategory(SHARED_SLUG[id]).fg,
  ]),
) as Record<AskCategoryId, string>;

/* The inverse. AskOurDataSection filters on Ask ids but the shared category
   dropdown speaks StudyCategorySlug, so the boundary translates both ways. */
export const ASK_ID_FOR_SLUG = Object.fromEntries(
  (Object.keys(SHARED_SLUG) as AskCategoryId[]).map((id) => [
    SHARED_SLUG[id],
    id,
  ]),
) as Record<StudyCategorySlug, AskCategoryId>;
